import React, { useEffect, useMemo, useRef, useState } from "react";
import { Motif } from "@/lib/motifs";

/**
 * Motif Magic — when a product page opens, the product's motif animates
 * along a real motion path with lens-matched blur. Then settles as a static
 * quiet mark in the corner.
 */
const MotifMagic = ({ motifKey }) => {
  const [phase, setPhase] = useState("fly"); // 'fly' | 'settle'
  const wrapRef = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setPhase("settle"), 3200);
    return () => clearTimeout(t);
  }, []);

  // 3 waypoints that trace a soft S across the plinth
  const path = useMemo(() => [
    { x: -10, y: 80, r: -18, s: 0.6, blur: 4, o: 0.0 },
    { x: 25, y: 40, r: -10, s: 0.9, blur: 2, o: 1.0 },
    { x: 60, y: 60, r: 12, s: 1.05, blur: 1, o: 1.0 },
    { x: 82, y: 25, r: 24, s: 0.85, blur: 3, o: 0.55 },
  ], []);
  const [step, setStep] = useState(0);
  useEffect(() => {
    if (phase !== "fly") return;
    let i = 0;
    const ti = setInterval(() => {
      i += 1;
      setStep(i);
      if (i >= path.length - 1) clearInterval(ti);
    }, 800);
    return () => clearInterval(ti);
  }, [phase, path.length]);

  if (phase === "settle") {
    return (
      <div className="absolute right-6 bottom-6 opacity-60" data-testid="motif-magic-settled">
        <Motif name={motifKey} size={22} strokeColor="var(--accent)" />
      </div>
    );
  }

  const p = path[step] || path[0];
  return (
    <div ref={wrapRef} className="absolute inset-0 pointer-events-none" data-testid="motif-magic">
      <div
        className="absolute"
        style={{
          left: `${p.x}%`,
          top: `${p.y}%`,
          transform: `translate(-50%, -50%) rotate(${p.r}deg) scale(${p.s})`,
          filter: `blur(${p.blur}px)`,
          opacity: p.o,
          transition: "left 900ms cubic-bezier(.34,.72,.36,1), top 900ms cubic-bezier(.34,.72,.36,1), transform 900ms cubic-bezier(.34,.72,.36,1), filter 900ms ease, opacity 900ms ease",
        }}
      >
        <Motif name={motifKey} size={54} strokeColor="var(--accent)" />
      </div>
    </div>
  );
};

export default MotifMagic;
